import {
  GDTMessageStatus,
  HL7MessageDirection,
  HL7MessageType,
  QueryFactory,
  SortDirection,
  TransportProtocol,
} from '@lmt/shared-types';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  pagingQueryParams,
  sortingQueryParams,
  toOffset,
} from '@lmt/unicorn-shared/app/components/common/table/operations';
import { TablePagination } from '@lmt/unicorn-shared/app/components/common/table/TablePagination';
import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import { PageSize } from '@lmt/unicorn-shared/app/components/common/table/types';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { DateTimeParamUnicorn } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { triggerDownloadFileDialog } from '@lmt/unicorn-shared/app/helpers/file-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import { useEventCallback } from '@mui/material';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import i18next from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumberParam, StringParam } from 'react-router-url-params';
import { ToggleButton } from '../../common/ui-kit/ToggleButton';
import { TransporterState } from '../../gdt/messages/components/TransporterState';
import { MessagesFilter } from './components/MessagesFilter';
import { MessagesTable } from './components/MessagesTable';
import styles from './HL7MessagesPage.module.scss';

const defaultPerPage = PageSize.Small;

export const HL7MessagesPage: React.FC = React.memo(function HL7MessagesPage() {
  const [queryParams, setQueryParams] = useQueryParamsInTab({
    ...pagingQueryParams(defaultPerPage),
    ...sortingQueryParams('id', true),
    from: DateTimeParamUnicorn,
    to: DateTimeParamUnicorn,
    search: StringParam,
    type: StringParam,
    direction: StringParam,
    status: StringParam,
    patientId: NumberParam,
    searchByError: StringParam,
  });
  const [isUpdating, setUpdating] = useState<boolean>(true);
  const [maxDateTime, setMaxDateTime] = useState<Date>(new Date());
  const [searchKey, setSearchKey] = useState(1);
  const updateFilters = useEventCallback(
    (params: Parameters<typeof setQueryParams>[0]) => {
      setQueryParams({ ...params, page: 0 });
      setMaxDateTime(new Date());
    },
  );
  const onResetFilters = useEventCallback(() => {
    updateFilters({
      from: null,
      to: null,
      search: null,
      type: null,
      direction: null,
      status: null,
      patientId: null,
      searchByError: null,
    });
    setSearchKey((patientSearchKey) => ++patientSearchKey);
  });
  const i18n = useScopedTranslation('hl7.messages');
  const modals = useModal();
  const queryCache = useQueryClient();

  const hl7OutgoingSettingsQuery =
    QueryFactory.HL7OutgoingSettingsQuery.useGetQuery();

  const hl7IncomingSettingsQuery =
    QueryFactory.HL7IncomingSettingsQuery.useGetQuery();

  const filterTo = useMemo(() => {
    if (isUpdating) {
      return queryParams.to;
    }
    if (!queryParams.to) {
      return maxDateTime;
    }
    return queryParams.to > maxDateTime ? maxDateTime : queryParams.to;
  }, [maxDateTime, queryParams.to, isUpdating]);

  const { data: messages, isFetching } =
    QueryFactory.HL7MessageQuery.useGetMessagesQuery(
      {
        from: queryParams.from,
        to: filterTo,
        type: queryParams.type as unknown as HL7MessageType | null,
        direction:
          queryParams.direction as unknown as HL7MessageDirection | null,
        status: queryParams.status as unknown as GDTMessageStatus | null,
        search: queryParams.search,
        searchByError: queryParams.searchByError,
        patientId: queryParams.patientId,
        pagination_Offset: toOffset(
          queryParams.page,
          queryParams.perPage,
          defaultPerPage,
        ),
        pagination_Limit: queryParams.perPage,
        sorting_SortField: queryParams.sortBy,
        sorting_SortDirection: queryParams.desc
          ? SortDirection.Desc
          : SortDirection.Asc,
        limitContentLength: true,
      },
      {
        placeholderData: keepPreviousData,
      },
    );

  useEffect(() => {
    if (!isUpdating) {
      setMaxDateTime(new Date());
    }
  }, [isUpdating]);

  const fileIds: string[] = useMemo(() => {
    if (messages) {
      const allFileIds: string[] = messages.data
        .filter((message) => !!message.outgoingFileId)
        .map((message) => message.outgoingFileId!);
      return [...new Set(allFileIds)];
    }
    return [];
  }, [messages]);

  const { data: documents, isFetching: isDocumentsFetching } =
    QueryFactory.FileQuery.useSearchFilesQuery({
      values: {
        HL7WasSent: ['true'],
      },
      isAllVersions: true,
      fileIds: fileIds,
    });
  const [isLoading, setIsLoading] = useState(false);

  const onDelete = useEventCallback(async (messageId: number) => {
    const result = await modals.showConfirm({
      title: i18n.t('options.confirmations.remove_title'),
      text: i18n.t('options.confirmations.remove_text'),
      okButtonText: i18n.t('options.confirmations.remove_ok_button'),
    });
    if (result) {
      try {
        await QueryFactory.HL7MessageClient.remove(messageId);
        await queryCache.invalidateQueries({
          queryKey: QueryFactory.HL7MessageQuery.getMessagesQueryKey(),
        });
      } catch (error) {
        await modals.showError({ text: convertToErrorString(error) });
      }
    }
  });

  async function reparseWithFilters() {
    try {
      if (messages && messages?.totalCount > 25) {
        const isConfirmed =
          (await modals.showConfirm({
            title: i18n.t('confirmations.reparse_title'),
            text: i18n.t('confirmations.reparse_text'),
            okButtonText: i18n.t('confirmations.reparse_ok_button'),
          })) ?? false;
        if (!isConfirmed) {
          return;
        }
      }
      setIsLoading(true);
      await QueryFactory.HL7MessageClient.reprocessMessage(
        undefined,
        queryParams.from,
        queryParams.to,
        queryParams.direction as unknown as HL7MessageDirection | null,
        queryParams.type as unknown as HL7MessageType | null,
        queryParams.status as unknown as GDTMessageStatus | null,
        queryParams.search,
        queryParams.searchByError,
        queryParams.patientId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
      );
      await queryCache.invalidateQueries({
        queryKey: QueryFactory.HL7MessageQuery.getMessagesQueryKey(),
      });
    } catch (e) {
      await modals.showError({ text: convertToErrorString(e) });
    } finally {
      setIsLoading(false);
    }
  }

  const onDownloadMessage = useEventCallback(async (messageId: number) => {
    try {
      const messages =
        await QueryFactory.HL7MessageClient.getMessages(messageId);
      const message = messages.data[0];
      await triggerDownloadFileDialog(
        new Blob([message.content]),
        `hl7-${message.patient?.id}-${format(
          message.dateTime,
          'yyyy-MM-dd-HH-mm-ss',
        )}.txt`,
      );
    } catch (e) {
      await modals.showError({ text: convertToErrorString(e) });
    }
  });

  const onReparseMessage = useEventCallback(async (messageId: number) => {
    try {
      await QueryFactory.HL7MessageClient.reprocessMessage(
        messageId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      );
      await queryCache.invalidateQueries({
        queryKey: QueryFactory.HL7MessageQuery.getMessagesQueryKey(),
      });
    } catch (e) {
      await modals.showError({ text: convertToErrorString(e) });
    }
  });

  const onReparseMessageWithFilter = useEventCallback(
    async (messageId: number) => {
      try {
        await QueryFactory.HL7MessageClient.reprocessMessage(
          messageId,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          true,
        );
        await queryCache.invalidateQueries({
          queryKey: QueryFactory.HL7MessageQuery.getMessagesQueryKey(),
        });
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    },
  );

  const onCopyText = useEventCallback(async (text: string) => {
    await navigator.clipboard.writeText(text);
  });

  const onShowFullMessage = useEventCallback(async (messageId: number) => {
    try {
      const messages =
        await QueryFactory.HL7MessageClient.getMessages(messageId);
      const message = messages.data[0];
      await modals.showCustom({
        title: '',
        Component: () => (
          <div className={styles.modalContentContainer}>{message.content}</div>
        ),
        Controls: (props) => (
          <div className={styles.modalButtonsContainer}>
            <Button
              title={i18n.t('options.copy_to_clipboard_button')}
              onClick={() => onCopyText(message.content)}
              color={ButtonColor.Secondary}
            />
            <Button
              title={i18next.t('uikit.dialog.ok_button')}
              type={'submit'}
              autoFocus={true}
              onClick={props.onClose}
            />
          </div>
        ),
      });
    } catch (e) {
      await modals.showError({ text: convertToErrorString(e) });
    }
  });

  return (
    <div className={styles.pageContainer} data-test-id="table-page-container">
      <div className={styles.tableToolbar}>
        <div className={styles.toolbar}>
          <TableTitle title={i18n.t('title')} count={messages?.totalCount} />
          {(hl7OutgoingSettingsQuery.data?.outgoingTransporterEnabled ||
            hl7IncomingSettingsQuery.data?.incomingTransporterEnabled) && (
            <TransporterState protocol={TransportProtocol.HL7} />
          )}
        </div>
        <div className={styles.toolbar}>
          <div className={styles.toggle}>
            <ToggleButton
              name={'hl7-real-time-updating'}
              checked={isUpdating}
              title={i18n.t('real_time_updating')}
              onChange={(e) => setUpdating(e.currentTarget.checked)}
            />
          </div>
          <Button
            title={i18n.t('reparse_button')}
            onClick={reparseWithFilters}
          />
        </div>
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.table}>
          <Loading
            loading={
              (isFetching && isUpdating) ||
              isLoading ||
              (isDocumentsFetching && isUpdating)
            }
            title={i18n.t('loading')}
          >
            <MessagesTable
              data={messages?.data ?? []}
              documents={documents ?? []}
              noResults={!!messages && messages.totalCount === 0}
              sorting={queryParams}
              changeSorting={updateFilters}
              reparseMessage={onReparseMessage}
              onDownloadMessage={onDownloadMessage}
              reparseMessageWithFilter={onReparseMessageWithFilter}
              onDelete={onDelete}
              onShowFullMessage={onShowFullMessage}
              searchText={queryParams.search}
            />
          </Loading>
        </div>
        <TablePagination
          pagination={useMemo(
            () => ({ page: queryParams.page, perPage: queryParams.perPage }),
            [queryParams.page, queryParams.perPage],
          )}
          pageLimitTitle={i18n.t('pagination_page_limit')}
          changePagination={setQueryParams}
          totalCount={messages?.totalCount ?? 0}
        />
      </div>
      <div className={styles.filtersBlockContainer}>
        <MessagesFilter
          from={queryParams.from ?? null}
          onFromChanged={useEventCallback((value) => {
            updateFilters({ from: value });
          })}
          to={queryParams.to ?? null}
          onToChanged={useEventCallback((value) => {
            updateFilters({ to: value });
          })}
          messageType={queryParams.type as unknown as HL7MessageType | null}
          onMessageTypeChanged={useEventCallback((value) => {
            updateFilters({ type: value?.toString() });
          })}
          direction={
            queryParams.direction as unknown as HL7MessageDirection | null
          }
          onDirectionChanged={useEventCallback((value) => {
            updateFilters({ direction: value?.toString() });
          })}
          status={queryParams.status as unknown as GDTMessageStatus | null}
          onStatusChanged={useEventCallback((value) => {
            updateFilters({ status: value?.toString() });
          })}
          search={queryParams.search ?? null}
          onSearchChanged={useEventCallback((search) => {
            updateFilters({ search: search });
          })}
          searchByError={queryParams.searchByError ?? null}
          onSearchByErrorChanged={useEventCallback((searchByError) => {
            updateFilters({ searchByError: searchByError });
          })}
          onReset={onResetFilters}
          searchKey={searchKey}
          patientId={queryParams.patientId ?? null}
          onPatientChanged={useEventCallback((value) => {
            updateFilters({ patientId: value?.id });
          })}
        />
      </div>
    </div>
  );
});
